'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from '@/shared/navigation'
import {
  dispatchKitchenTransfer,
  fetchPeerKitchens,
  fetchTodayKitchenTransfers,
  newTransferCode,
  receiveKitchenTransfer,
  saveKitchenTransfer,
  scanKitchenTransferQr,
  fetchKitchenMaterialStock,
  type ApiKitchenTransfer,
  type ApiKitchenTransferLine,
  type KitchenTransferLineInput,
} from '@/features/kitchen/api/kitchenTransferApi'
import { fetchKitchenDelmans, fetchKitchenStockLots } from '@/features/kitchen/api/getDispatchOptions'
import { shouldUseKitchenApi } from '@/features/kitchen/api/kitchenApiMode'
import { PinKeypadModal } from '@/shared/components/PinKeypadModal'
import { ActionSummaryCard } from '@/shared/components/ActionSummaryCard'
import { EmptyState } from '@/shared/components/EmptyState'
import { FlowStepper } from '@/shared/components/FlowStepper'
import { QtyStepper } from '@/shared/components/QtyStepper'
import { SkeletonList } from '@/shared/components/Skeleton'
import { StickyActionFooter } from '@/shared/components/StickyActionFooter'
import { Toast } from '@/shared/components/Toast'
import { getKitchenDevice } from '@/shared/auth/deviceStorage'

type Tab = 'send' | 'receive'
type SendView = 'list' | 'edit' | 'review'
type ReceiveView = 'list' | 'scan' | 'confirm'

type StockLot = {
  productName: string
  productionDate: string
  stockAvailable: number
}

type SendItemTab = 'product' | 'material'

type MaterialStockLot = {
  materialName: string
  uom: string
  stockAvailable: number
}

type EditorLine = KitchenTransferLineInput & { maxQty: number }

type ReceiveLineState = {
  itemType: 'product' | 'material'
  productName?: string
  materialName?: string
  productionDate?: string
  uom?: string
  planned: number
  qtyGood: number
}

const SEND_FLOW_STEPS = [{ label: 'Daftar' }, { label: 'Isi kiriman' }, { label: 'Serah delman' }] as const
const RECEIVE_FLOW_STEPS = [{ label: 'Daftar' }, { label: 'Scan QR' }, { label: 'Konfirmasi' }] as const

function sendFlowStep(view: SendView): number {
  if (view === 'review') return 3
  if (view === 'edit') return 2
  return 1
}

function receiveFlowStep(view: ReceiveView): number {
  if (view === 'confirm') return 3
  if (view === 'scan') return 2
  return 1
}

function transferStatusLabel(status: string): string {
  if (status === 'draft') return 'Draft'
  if (status === 'in_transit') return 'Dalam perjalanan'
  if (status === 'received') return 'Sudah diterima'
  return status
}

function transferStatusBadge(status: string): string {
  if (status === 'draft') return 'badge-draft'
  if (status === 'in_transit') return 'badge-warning'
  if (status === 'received') return 'badge-success'
  return 'badge-draft'
}

function formatProductionDate(iso: string): string {
  if (!iso) return '—'
  try {
    return new Intl.DateTimeFormat('id-ID', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    }).format(new Date(`${iso.slice(0, 10)}T12:00:00`))
  } catch {
    return iso
  }
}

function formatTransferSummary(lines: ApiKitchenTransferLine[]): string {
  const totalQty = lines.reduce((sum, line) => sum + line.qty, 0)
  const productCount = lines.filter((l) => (l.item_type ?? 'product') === 'product').length
  const materialCount = lines.filter((l) => l.item_type === 'material').length
  const parts: string[] = []
  if (productCount > 0) parts.push(`${productCount} produk`)
  if (materialCount > 0) parts.push(`${materialCount} bahan`)
  if (parts.length === 0) return 'Belum ada baris'
  return `${parts.join(' · ')} · ${totalQty} qty`
}

function stockSummaryText(lots: StockLot[]): string {
  const available = lots.filter((lot) => lot.stockAvailable > 0)
  if (available.length === 0) return 'Belum ada stok bagus di dapur'
  const preview = available
    .slice(0, 3)
    .map((lot) => `${lot.productName} ${lot.stockAvailable}`)
    .join(' · ')
  const more = available.length > 3 ? ` +${available.length - 3} lainnya` : ''
  return preview + more
}

function filterEditableLines<T extends { maxQty: number; qty: number }>(lines: T[], showAll: boolean): T[] {
  if (showAll) return lines
  const withStock = lines.filter((line) => line.maxQty > 0 || line.qty > 0)
  return withStock.length > 0 ? withStock : lines.filter((line) => line.qty > 0)
}

export function KitchenTransferPage({ initialTab = 'send' }: { initialTab?: Tab }) {
  const device = getKitchenDevice()
  const useApi = shouldUseKitchenApi()
  const kitchenName = device?.kitchenName ?? 'Dapur'

  const [tab, setTab] = useState<Tab>(initialTab)
  const [sendView, setSendView] = useState<SendView>('list')
  const [receiveView, setReceiveView] = useState<ReceiveView>('list')
  const [loading, setLoading] = useState(true)
  const [outgoing, setOutgoing] = useState<ApiKitchenTransfer[]>([])
  const [incoming, setIncoming] = useState<ApiKitchenTransfer[]>([])
  const [peerKitchens, setPeerKitchens] = useState<{ id: string; name: string }[]>([])
  const [delmans, setDelmans] = useState<string[]>([])
  const [stockLots, setStockLots] = useState<StockLot[]>([])
  const [materialStock, setMaterialStock] = useState<MaterialStockLot[]>([])
  const [sendItemTab, setSendItemTab] = useState<SendItemTab>('product')
  const [showAllSendLines, setShowAllSendLines] = useState(false)

  const [transferCode, setTransferCode] = useState('')
  const [targetKitchen, setTargetKitchen] = useState('')
  const [delmanName, setDelmanName] = useState('')
  const [productLines, setProductLines] = useState<EditorLine[]>([])
  const [materialLines, setMaterialLines] = useState<EditorLine[]>([])

  const [activeReceive, setActiveReceive] = useState<ApiKitchenTransfer | null>(null)
  const [receiveLines, setReceiveLines] = useState<ReceiveLineState[]>([])

  const [pinOpen, setPinOpen] = useState(false)
  const [pinAction, setPinAction] = useState<'dispatch' | 'receive'>('dispatch')
  const [toast, setToast] = useState<string | null>(null)
  const [scanToken, setScanToken] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  const todayLabel = useMemo(
    () =>
      new Intl.DateTimeFormat('id-ID', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(new Date()),
    [],
  )

  const reload = useCallback(async () => {
    if (!device?.deviceId) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const [transfers, peers, delmanList, stock, matStock] = await Promise.all([
        fetchTodayKitchenTransfers(device.deviceId),
        fetchPeerKitchens(device.deviceId),
        fetchKitchenDelmans(device.deviceId),
        fetchKitchenStockLots(device.deviceId),
        fetchKitchenMaterialStock(device.deviceId),
      ])
      setOutgoing(transfers.outgoing)
      setIncoming(transfers.incoming)
      setPeerKitchens(peers.map((p) => ({ id: p.id, name: p.name })))
      setDelmans(delmanList.map((d) => d.name))
      setStockLots(
        stock.map((s) => ({
          productName: s.productName,
          productionDate: s.productionDate,
          stockAvailable: s.stockAvailable,
        })),
      )
      setMaterialStock(matStock)
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Gagal memuat data')
    } finally {
      setLoading(false)
    }
  }, [device?.deviceId])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    if (!device?.deviceId) return undefined
    const hasPendingOutgoing = outgoing.some((item) => item.status === 'in_transit')
    const hasPendingIncoming = incoming.some((item) => item.status !== 'received')
    if (!hasPendingOutgoing && !hasPendingIncoming) return undefined
    const timer = window.setInterval(() => void reload(), 5_000)
    return () => window.clearInterval(timer)
  }, [device?.deviceId, outgoing, incoming, reload])

  useEffect(() => {
    const onRefresh = () => void reload()
    const onVisible = () => {
      if (document.visibilityState === 'visible') void reload()
    }
    window.addEventListener('focus', onRefresh)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('focus', onRefresh)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [reload])

  const draftOutgoing = useMemo(() => outgoing.filter((item) => item.status === 'draft'), [outgoing])
  const sentOutgoing = useMemo(() => outgoing.filter((item) => item.status !== 'draft'), [outgoing])
  const receivableIncoming = useMemo(
    () => incoming.filter((item) => item.status !== 'received'),
    [incoming],
  )

  const visibleProductLines = useMemo(
    () => filterEditableLines(productLines, showAllSendLines),
    [productLines, showAllSendLines],
  )
  const visibleMaterialLines = useMemo(
    () => filterEditableLines(materialLines, showAllSendLines),
    [materialLines, showAllSendLines],
  )

  const sendPayloadLines = useMemo(() => {
    const products = productLines
      .filter((l) => l.qty > 0)
      .map(({ productName, productionDate, qty }) => ({
        itemType: 'product' as const,
        productName,
        productionDate,
        qty,
      }))
    const materials = materialLines
      .filter((l) => l.qty > 0)
      .map(({ materialName, uom, qty }) => ({
        itemType: 'material' as const,
        materialName,
        uom,
        qty,
      }))
    return [...products, ...materials]
  }, [productLines, materialLines])

  const sendTotalQty = useMemo(
    () => sendPayloadLines.reduce((sum, line) => sum + line.qty, 0),
    [sendPayloadLines],
  )

  function buildEditorLinesFromStock() {
    setProductLines(
      stockLots.map((lot) => ({
        itemType: 'product' as const,
        productName: lot.productName,
        productionDate: lot.productionDate,
        qty: 0,
        maxQty: lot.stockAvailable,
      })),
    )
    setMaterialLines(
      materialStock.map((lot) => ({
        itemType: 'material' as const,
        materialName: lot.materialName,
        uom: lot.uom,
        qty: 0,
        maxQty: lot.stockAvailable,
      })),
    )
  }

  function startNewTransfer() {
    if (peerKitchens.length === 0) {
      setToast('Belum ada dapur lain di brand ini.')
      return
    }
    if (delmans.length === 0) {
      setToast('Belum ada delman terdaftar.')
      return
    }
    const code = newTransferCode()
    setTransferCode(code)
    setTargetKitchen(peerKitchens[0]?.name ?? '')
    setDelmanName(delmans[0] ?? '')
    buildEditorLinesFromStock()
    setSendItemTab('product')
    setShowAllSendLines(false)
    setFormError(null)
    setSendView('edit')
  }

  function openTransferEdit(item: ApiKitchenTransfer) {
    setTransferCode(item.code)
    setTargetKitchen(item.target_kitchen_name ?? '')
    setDelmanName(item.delman_name)
    const prod = item.lines
      .filter((l) => (l.item_type ?? 'product') === 'product')
      .map((l) => {
        const lot = stockLots.find(
          (s) => s.productName === l.product_name && s.productionDate === l.production_date,
        )
        return {
          itemType: 'product' as const,
          productName: l.product_name ?? '',
          productionDate: l.production_date ?? '',
          qty: l.qty,
          maxQty: lot?.stockAvailable ?? l.qty,
        }
      })
    const mat = item.lines
      .filter((l) => l.item_type === 'material')
      .map((l) => {
        const lot = materialStock.find((s) => s.materialName === l.material_name && s.uom === l.uom)
        return {
          itemType: 'material' as const,
          materialName: l.material_name ?? '',
          uom: l.uom ?? 'kg',
          qty: l.qty,
          maxQty: lot?.stockAvailable ?? l.qty,
        }
      })
    setProductLines(
      prod.length > 0
        ? prod
        : stockLots.map((lot) => ({
            itemType: 'product' as const,
            productName: lot.productName,
            productionDate: lot.productionDate,
            qty: 0,
            maxQty: lot.stockAvailable,
          })),
    )
    setMaterialLines(
      mat.length > 0
        ? mat
        : materialStock.map((lot) => ({
            itemType: 'material' as const,
            materialName: lot.materialName,
            uom: lot.uom,
            qty: 0,
            maxQty: lot.stockAvailable,
          })),
    )
    setFormError(null)
    setSendView('edit')
  }

  function validateSendForm(): boolean {
    if (!targetKitchen.trim()) {
      setFormError('Pilih dapur tujuan.')
      return false
    }
    if (!delmanName.trim()) {
      setFormError('Pilih delman.')
      return false
    }
    if (sendPayloadLines.length === 0) {
      setFormError('Isi minimal 1 produk atau bahan baku dengan qty lebih dari 0.')
      return false
    }
    setFormError(null)
    return true
  }

  function openSendReview() {
    if (!validateSendForm()) return
    setSendView('review')
  }

  async function handleSaveDraft() {
    if (!device?.deviceId || !validateSendForm()) return
    try {
      await saveKitchenTransfer(transferCode, {
        targetKitchenName: targetKitchen,
        delmanName,
        lines: sendPayloadLines,
      })
      setToast('Draft tersimpan.')
      setSendView('list')
      await reload()
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Gagal menyimpan')
    }
  }

  async function handleDispatch(pin: string) {
    if (!device?.deviceId) return
    try {
      await dispatchKitchenTransfer(transferCode, {
        targetKitchenName: targetKitchen,
        delmanName,
        lines: sendPayloadLines,
        pin,
      })
      setToast('Diserahkan ke delman.')
      setPinOpen(false)
      setSendView('list')
      await reload()
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Gagal serah ke delman')
    }
  }

  function openReceiveConfirm(item: ApiKitchenTransfer) {
    setActiveReceive(item)
    setReceiveLines(
      item.lines.map((l) => {
        const isMaterial = l.item_type === 'material'
        return {
          itemType: isMaterial ? ('material' as const) : ('product' as const),
          productName: l.product_name,
          materialName: l.material_name,
          productionDate: l.production_date,
          uom: l.uom,
          planned: l.qty,
          qtyGood: l.qty_good && l.qty_good > 0 ? l.qty_good : l.qty,
        }
      }),
    )
    setFormError(null)
    setReceiveView('confirm')
  }

  async function handleScanSubmit() {
    if (!scanToken.trim()) {
      setToast('Masukkan token QR dari delman.')
      return
    }
    try {
      const res = await scanKitchenTransferQr(scanToken.trim())
      const match = incoming.find((i) => i.id === res.transfer_id || i.code === res.transfer_code)
      if (match) {
        openReceiveConfirm(match)
        setScanToken('')
        return
      }
      await reload()
      const refreshed = await fetchTodayKitchenTransfers(device!.deviceId)
      const found =
        refreshed.incoming.find((i) => i.id === res.transfer_id) ??
        refreshed.incoming.find((i) => i.code === res.transfer_code)
      if (found) {
        openReceiveConfirm(found)
        setScanToken('')
      } else {
        setToast(`Scan berhasil — ${res.source_kitchen_name}. Buka dari daftar masuk.`)
        setReceiveView('list')
      }
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'QR tidak valid')
    }
  }

  async function handleReceive(pin: string) {
    if (!activeReceive) return
    try {
      await receiveKitchenTransfer(
        activeReceive.code,
        pin,
        receiveLines.map((l) => ({
          itemType: l.itemType,
          productName: l.productName,
          materialName: l.materialName,
          productionDate: l.productionDate,
          uom: l.uom,
          qtyGood: l.qtyGood,
        })),
      )
      setToast('Transfer diterima.')
      setPinOpen(false)
      setReceiveView('list')
      setActiveReceive(null)
      await reload()
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Gagal konfirmasi terima')
    }
  }

  function setReceiveLine(idx: number, patch: Partial<ReceiveLineState>) {
    setReceiveLines((prev) => prev.map((line, i) => (i === idx ? { ...line, ...patch } : line)))
  }

  function applyReceiveAsPlanned(idx: number) {
    setReceiveLines((prev) =>
      prev.map((line, i) =>
        i === idx ? { ...line, qtyGood: line.planned } : line,
      ),
    )
  }

  const pageTitle = tab === 'send' ? 'Kirim ke dapur lain' : 'Terima dari dapur lain'
  const pageSubtitle =
    tab === 'send'
      ? 'Stok bagus dapur → delman → dapur tujuan'
      : 'Scan QR delman lalu konfirmasi penerimaan'

  return (
    <section className="space-y-4 pb-24">
      <div className="card">
        <Link href="/kitchen/home" className="text-body-sm text-primary inline-flex items-center gap-1 mb-2">
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          Home
        </Link>
        <p className="text-headline-md text-primary">{pageTitle}</p>
        <p className="text-body-sm text-on-surface-variant mt-1">{pageSubtitle}</p>
        <p className="text-label-md text-on-surface-variant mt-1 capitalize">
          {kitchenName} · {todayLabel}
        </p>
        <div className="mt-3 pt-3 border-t border-surface-variant">
          <FlowStepper
            steps={tab === 'send' ? [...SEND_FLOW_STEPS] : [...RECEIVE_FLOW_STEPS]}
            activeStep={tab === 'send' ? sendFlowStep(sendView) : receiveFlowStep(receiveView)}
          />
        </div>
      </div>

      <div className="card p-2">
        <div className="grid grid-cols-2 gap-2">
          <Link
            href="/kitchen/kitchen-transfer"
            className={`rounded-lg px-3 py-2 text-body-sm font-semibold min-h-[44px] text-center ${
              tab === 'send'
                ? 'bg-primary-container text-on-primary-container'
                : 'text-on-surface-variant bg-surface'
            }`}
            onClick={() => {
              setTab('send')
              setSendView('list')
            }}
          >
            Kirim
          </Link>
          <Link
            href="/kitchen/kitchen-transfer/receive"
            className={`rounded-lg px-3 py-2 text-body-sm font-semibold min-h-[44px] text-center ${
              tab === 'receive'
                ? 'bg-primary-container text-on-primary-container'
                : 'text-on-surface-variant bg-surface'
            }`}
            onClick={() => {
              setTab('receive')
              setReceiveView('list')
            }}
          >
            Terima
          </Link>
        </div>
      </div>

      {loading && <SkeletonList count={3} label="Memuat transfer hari ini" />}

      {!loading && tab === 'send' && sendView === 'list' && (
        <div className="space-y-4">
          {draftOutgoing.length > 0 && (
            <div className="space-y-2">
              <p className="text-label-md font-semibold text-on-surface-variant">Belum diserahkan ke delman</p>
              {draftOutgoing.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="card w-full text-left hover:border-primary transition-colors"
                  onClick={() => openTransferEdit(item)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-body-lg font-semibold text-on-surface">→ {item.target_kitchen_name}</p>
                      <p className="text-body-sm text-on-surface-variant mt-1">
                        {formatTransferSummary(item.lines)} · {item.delman_name}
                      </p>
                      <p className="text-label-md font-mono text-on-surface-variant mt-1">{item.code}</p>
                    </div>
                    <span className={transferStatusBadge(item.status)}>{transferStatusLabel(item.status)}</span>
                  </div>
                  <p className="text-body-sm text-primary font-semibold mt-2">Lanjut isi</p>
                </button>
              ))}
            </div>
          )}

          {sentOutgoing.length > 0 && (
            <div className="space-y-2">
              <p className="text-label-md font-semibold text-on-surface-variant">Sudah ke delman</p>
              {sentOutgoing.map((item) => (
                <div key={item.id} className="card">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-body-lg font-semibold text-on-surface">→ {item.target_kitchen_name}</p>
                      <p className="text-body-sm text-on-surface-variant mt-1">
                        {formatTransferSummary(item.lines)} · {item.delman_name}
                      </p>
                    </div>
                    <span className={transferStatusBadge(item.status)}>{transferStatusLabel(item.status)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {outgoing.length === 0 && (
            <EmptyState
              icon="swap_horiz"
              title="Belum ada kiriman hari ini"
              description="Buat kiriman baru ke dapur lain. Hanya stok bagus yang bisa dipindahkan."
            />
          )}

          <button type="button" className="btn-primary w-full min-h-[44px]" onClick={startNewTransfer}>
            + Kirim ke dapur lain
          </button>
        </div>
      )}

      {!loading && tab === 'send' && sendView === 'edit' && (
        <div className="space-y-4">
          <div className="card space-y-3">
            <p className="text-headline-md text-primary">Kirim → {targetKitchen || '…'}</p>
            <p className="text-label-md font-mono text-on-surface-variant">{transferCode}</p>

            <div className="rounded-lg bg-surface-container p-3 text-body-sm">
              <p className="text-on-surface-variant">Stok bagus tersedia</p>
              <p className="text-on-surface mt-1">{stockSummaryText(stockLots)}</p>
            </div>

            <div className="space-y-2">
              <label className="field-label" htmlFor="kt_target_kitchen">
                Dapur tujuan
              </label>
              <select
                id="kt_target_kitchen"
                className="input-field"
                value={targetKitchen}
                onChange={(e) => setTargetKitchen(e.target.value)}
              >
                {peerKitchens.map((k) => (
                  <option key={k.id} value={k.name}>
                    {k.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="field-label" htmlFor="kt_delman">
                Delman
              </label>
              <select
                id="kt_delman"
                className="input-field"
                value={delmanName}
                onChange={(e) => setDelmanName(e.target.value)}
              >
                {delmans.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="card p-2">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                className={`rounded-lg px-3 py-2 text-body-sm font-semibold min-h-[44px] ${
                  sendItemTab === 'product'
                    ? 'bg-secondary-container text-on-secondary-container'
                    : 'text-on-surface-variant bg-surface'
                }`}
                onClick={() => setSendItemTab('product')}
              >
                Produk jadi
              </button>
              <button
                type="button"
                className={`rounded-lg px-3 py-2 text-body-sm font-semibold min-h-[44px] ${
                  sendItemTab === 'material'
                    ? 'bg-secondary-container text-on-secondary-container'
                    : 'text-on-surface-variant bg-surface'
                }`}
                onClick={() => setSendItemTab('material')}
              >
                Bahan baku
              </button>
            </div>
          </div>

          <div className="card space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-label-md text-on-surface-variant">
                {sendItemTab === 'product' ? 'Qty per lot produksi' : 'Qty bahan baku'}
              </p>
              <button
                type="button"
                className="text-body-sm text-primary font-semibold"
                onClick={() => setShowAllSendLines((prev) => !prev)}
              >
                {showAllSendLines ? 'Sembunyikan stok 0' : 'Tampilkan semua'}
              </button>
            </div>

            {sendItemTab === 'product' && visibleProductLines.length === 0 && (
              <EmptyState
                compact
                icon="inventory_2"
                title="Tidak ada stok produk"
                description="Produksi dulu atau pilih tab bahan baku jika yang dikirim bahan mentah."
              />
            )}

            {sendItemTab === 'product' &&
              visibleProductLines.map((line) => {
                const realIdx = productLines.findIndex(
                  (l) =>
                    l.productName === line.productName && l.productionDate === line.productionDate,
                )
                return (
                  <div
                    key={`${line.productName}-${line.productionDate}`}
                    className="flex items-center justify-between gap-3 border-b border-surface-variant pb-3 last:border-0 last:pb-0"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-body-sm font-semibold text-on-surface">{line.productName}</p>
                      <p className="text-label-md text-on-surface-variant">
                        Prod. {formatProductionDate(line.productionDate ?? '')} · max {line.maxQty} pcs
                      </p>
                    </div>
                    <QtyStepper
                      value={line.qty}
                      min={0}
                      max={line.maxQty}
                      disabled={line.maxQty <= 0}
                      onChange={(qty) => {
                        setProductLines((prev) =>
                          prev.map((l, i) => (i === realIdx ? { ...l, qty } : l)),
                        )
                      }}
                    />
                  </div>
                )
              })}

            {sendItemTab === 'material' && visibleMaterialLines.length === 0 && (
              <p className="text-body-sm text-on-surface-variant">Tidak ada stok bahan baku tersedia.</p>
            )}

            {sendItemTab === 'material' &&
              visibleMaterialLines.map((line) => {
                const realIdx = materialLines.findIndex(
                  (l) => l.materialName === line.materialName && l.uom === line.uom,
                )
                return (
                  <div
                    key={`${line.materialName}-${line.uom}`}
                    className="flex items-center justify-between gap-3 border-b border-surface-variant pb-3 last:border-0 last:pb-0"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-body-sm font-semibold text-on-surface">{line.materialName}</p>
                      <p className="text-label-md text-on-surface-variant">
                        {line.uom} · max {line.maxQty}
                      </p>
                    </div>
                    <QtyStepper
                      value={line.qty}
                      min={0}
                      max={line.maxQty}
                      disabled={line.maxQty <= 0}
                      onChange={(qty) => {
                        setMaterialLines((prev) =>
                          prev.map((l, i) => (i === realIdx ? { ...l, qty } : l)),
                        )
                      }}
                    />
                  </div>
                )
              })}
          </div>

          {formError && (
            <div role="alert" className="alert-error">
              <span>{formError}</span>
            </div>
          )}

          <StickyActionFooter>
            <button type="button" className="btn-outline flex-1 min-h-[44px]" onClick={() => setSendView('list')}>
              Batal
            </button>
            <button type="button" className="btn-outline flex-1 min-h-[44px]" onClick={() => void handleSaveDraft()}>
              Simpan
            </button>
            <button type="button" className="btn-primary flex-1 min-h-[44px]" onClick={openSendReview}>
              Lanjut
            </button>
          </StickyActionFooter>
        </div>
      )}

      {!loading && tab === 'send' && sendView === 'review' && (
        <div className="space-y-4">
          <div className="card space-y-2">
            <p className="text-headline-md text-primary">Review kiriman</p>
            <p className="text-body-sm text-on-surface-variant">
              Ke <strong className="text-on-surface">{targetKitchen}</strong> · Delman{' '}
              <strong className="text-on-surface">{delmanName}</strong>
            </p>
          </div>

          <ActionSummaryCard
            items={sendPayloadLines.map((line, index) => ({
              id: `${line.itemType}-${index}`,
              title:
                line.itemType === 'material'
                  ? `${line.materialName} (${line.uom})`
                  : line.productName ?? '',
              subtitle:
                line.itemType === 'product'
                  ? `Prod. ${formatProductionDate(line.productionDate ?? '')}`
                  : 'Bahan baku',
              value: `${line.qty}${line.itemType === 'product' ? ' pcs' : ''}`,
            }))}
            totalLabel="Total kirim"
            totalValue={`${sendPayloadLines.length} baris · ${sendTotalQty} qty`}
          />

          <StickyActionFooter>
            <button type="button" className="btn-outline flex-1 min-h-[44px]" onClick={() => setSendView('edit')}>
              Kembali
            </button>
            <button
              type="button"
              className="btn-primary flex-1 min-h-[44px]"
              onClick={() => {
                setPinAction('dispatch')
                setPinOpen(true)
              }}
            >
              Serah ke delman
            </button>
          </StickyActionFooter>
        </div>
      )}

      {!loading && tab === 'receive' && receiveView === 'list' && (
        <div className="space-y-4">
          <button
            type="button"
            className="btn-primary w-full min-h-[44px] inline-flex items-center justify-center gap-2"
            onClick={() => setReceiveView('scan')}
          >
            <span className="material-symbols-outlined">qr_code_scanner</span>
            Scan QR delman
          </button>

          {receivableIncoming.length === 0 ? (
            <EmptyState
              icon="move_to_inbox"
              title="Belum ada kiriman masuk"
              description="Setelah delman scan di dapur asal, scan QR di sini lalu konfirmasi terima."
            />
          ) : (
            <div className="space-y-2">
              <p className="text-label-md font-semibold text-on-surface-variant">Menunggu diterima</p>
              {incoming.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="card w-full text-left hover:border-primary transition-colors disabled:opacity-60"
                  onClick={() => {
                    if (item.receivable) openReceiveConfirm(item)
                    else setToast('Scan QR delman dulu sebelum konfirmasi terima.')
                  }}
                  disabled={item.status === 'received'}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-body-lg font-semibold text-on-surface">
                        Dari {item.source_kitchen_name}
                      </p>
                      <p className="text-body-sm text-on-surface-variant mt-1">
                        {formatTransferSummary(item.lines)} · {item.delman_name}
                      </p>
                      <p className="text-label-md font-mono text-on-surface-variant mt-1">{item.code}</p>
                    </div>
                    <span className={transferStatusBadge(item.status)}>{transferStatusLabel(item.status)}</span>
                  </div>
                  {!item.receivable && item.status !== 'received' && (
                    <p className="text-body-sm text-status-warning mt-2">Perlu scan QR delman</p>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'receive' && receiveView === 'scan' && (
        <div className="card space-y-4">
          <EmptyState
            compact
            icon="qr_code_scanner"
            title="Scan QR dari delman"
            description="Delman menampilkan QR setelah barang diambil. Tempel token di bawah jika scan kamera belum tersedia."
          />
          <div className="space-y-2">
            <label className="field-label" htmlFor="kt_scan_token">
              Token QR
            </label>
            <input
              id="kt_scan_token"
              className="input-field font-mono"
              value={scanToken}
              onChange={(e) => setScanToken(e.target.value.toUpperCase())}
              placeholder="KTH-…"
              autoComplete="off"
            />
          </div>
          <button type="button" className="btn-primary w-full min-h-[44px]" onClick={() => void handleScanSubmit()}>
            Verifikasi scan
          </button>
          <button type="button" className="btn-outline w-full min-h-[44px]" onClick={() => setReceiveView('list')}>
            Batal
          </button>
        </div>
      )}

      {tab === 'receive' && receiveView === 'confirm' && activeReceive && (
        <div className="space-y-4">
          <div className="card space-y-2">
            <p className="text-headline-md text-primary">Terima dari {activeReceive.source_kitchen_name}</p>
            <p className="text-body-sm text-on-surface-variant">
              {activeReceive.code} · Delman {activeReceive.delman_name}
            </p>
            <p className="text-label-md text-on-surface-variant">
              Qty yang dikonfirmasi masuk stok dapur tujuan.
            </p>
          </div>

          {receiveLines.map((line, idx) => {
            const label =
              line.itemType === 'material'
                ? `${line.materialName} (${line.uom})`
                : `${line.productName} · prod. ${formatProductionDate(line.productionDate ?? '')}`
            const unit = line.itemType === 'product' ? 'pcs' : (line.uom ?? '')
            return (
              <div key={`${line.itemType}-${line.productName ?? line.materialName}-${idx}`} className="card space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-body-sm font-semibold text-on-surface">{label}</p>
                    <p className="text-label-md text-on-surface-variant">Rencana {line.planned} {unit}</p>
                  </div>
                  <button
                    type="button"
                    className="text-body-sm text-primary font-semibold shrink-0"
                    onClick={() => applyReceiveAsPlanned(idx)}
                  >
                    Sesuai rencana
                  </button>
                </div>
                <div className="space-y-1">
                  <p className="text-label-md text-on-surface-variant">Diterima</p>
                  <QtyStepper
                    value={line.qtyGood}
                    min={0}
                    max={line.planned}
                    onChange={(qtyGood) => setReceiveLine(idx, { qtyGood })}
                  />
                </div>
              </div>
            )
          })}

          <StickyActionFooter>
            <button type="button" className="btn-outline flex-1 min-h-[44px]" onClick={() => setReceiveView('list')}>
              Batal
            </button>
            <button
              type="button"
              className="btn-primary flex-1 min-h-[44px]"
              onClick={() => {
                setPinAction('receive')
                setPinOpen(true)
              }}
            >
              Konfirmasi terima
            </button>
          </StickyActionFooter>
        </div>
      )}

      <PinKeypadModal
        open={pinOpen}
        title={pinAction === 'dispatch' ? 'PIN serah ke delman' : 'PIN konfirmasi terima'}
        description="Masukkan PIN 6 digit kepala dapur."
        confirmLabel={pinAction === 'dispatch' ? 'Serah ke delman' : 'Konfirmasi terima'}
        onClose={() => setPinOpen(false)}
        onConfirm={async (pin) => {
          if (pinAction === 'dispatch') await handleDispatch(pin)
          else await handleReceive(pin)
        }}
      />

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}

      {!useApi && (
        <p className="text-caption text-on-surface-variant">Mode mock — aktifkan API untuk data live.</p>
      )}
    </section>
  )
}
